import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useGetFullServiceInvoiceByIdQuery, useGetUserParamsQuery } from '../../../api';
import useFullServiceInvoice from '../hooks/useFullServiceInvoiceData';
import FullServiceInvoiceForm from '../components/FullServiceInvoiceForm';
import { useLanguage } from '../../../contexts/LanguageContext';
import FullServiceKmTable from './component/FullServiceKmTable';
import FullServiceKmFormDialog from './component/FullServiceKmFormDialog';
import useFullServiceKmData from './hooks/useFullServiceKmData';
import { selectUserParams } from '../../../store/slices/userParamsSlice';
import { usePermissions } from '../../../hooks/usePermissions';
import { useSelector } from 'react-redux';

const FullServiceCreateEditPage = () => {
    const { th } = useLanguage();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const toast = useRef(null);

    const { data: response } = useGetUserParamsQuery();
    const userParamsFromStore = useSelector(selectUserParams);
    const userParams = userParamsFromStore || response || {};
    const { hasWarrantyTeamRole } = usePermissions();
    const { hasAdminRole } = usePermissions();
    const { hasWarrantyManagerRole } = usePermissions();
    const isWarranty = !!hasWarrantyTeamRole?.() || !!hasWarrantyManagerRole?.() || !!hasAdminRole?.();

    const isCanUseFullService = userParams.isCanUseFullServiceDealer;

    const { data: invoiceDetail, isLoading } = useGetFullServiceInvoiceByIdQuery(id, {
        skip: !isEdit
    });
    const { handleCreate, handleUpdate,handleSendToSap } = useFullServiceInvoice();

    const { handleCreateKm, handleUpdateKm, handleChangeStatus, updatingKm, changingStatus, refetch: refetchKmData } = useFullServiceKmData(id);

    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingRow, setEditingRow] = useState(null);

    const handleEditKm = (rowData) => {
        setEditingRow(rowData);
        setDialogVisible(true);
    };

    const handleCancel = (e) => {
        e?.preventDefault?.(); // form submit'i engelle
        e?.stopPropagation?.(); // bubbling'i kes
        navigate(-1); // geri dön
    };

    const dialogBusy = updatingKm || changingStatus;

    const upsertSilent = async (payload) => (payload?.id ? handleUpdateKm(payload) : handleCreateKm(payload));

    const handleSaveKmRow = async (payload) => {
        try {
            let result;
            if (payload?.id) {
                result = await handleUpdateKm(payload);
            } else {
                result = await handleCreateKm(payload);
            }

            if (result?.isSuccessful) {
                toast.current.show({
                    severity: 'success',
                    summary: th('fullServiceInvoice.toastMessages.successSummary'),
                    detail: payload?.id ? th('fullServiceInvoice.toastMessages.updated') : th('fullServiceInvoice.toastMessages.saved'),
                    life: 3000
                });

                setDialogVisible(false);
                setEditingRow(null);
                refetchKmData();
            } else {
                throw new Error();
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: th('fullServiceInvoice.toastMessages.errorSummary'),
                detail: th('fullServiceInvoice.toastMessages.saveFailed'),
                life: 3000
            });
        }
    };

    const handleSubmit = async (payload) => {
        const result = isEdit ? await handleUpdate(payload) : await handleCreate(payload);

        if (result?.isSuccessful) {
            navigate('/full-service-invoices');
        }

        return result;
    };
    

    return (
        <div className="card">
            <Toast ref={toast} />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{isEdit ? th('fullServiceInvoice.dialogHeader.edit') : th('fullServiceInvoice.dialogHeader.create')}</h1>
            </div>

            <FullServiceInvoiceForm formId="fs-invoice-form" initialData={invoiceDetail?.data} isCanUseFullService={isCanUseFullService} isWarranty={isWarranty} isEdit={isEdit} onSubmit={handleSubmit} onCancel={handleCancel} onSendToSap={handleSendToSap} loading={isLoading} />

            {isEdit && (
                <div className="mt-5 pt-4 border-top-1 surface-border">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h2 className="m-0">{th('fullServiceInvoice.sections.kmRecords')}</h2>
                        <Button
                            icon="pi pi-plus"
                            label={th('fullServiceInvoice.page.newKmButton')}
                            onClick={() => {
                                setEditingRow(null); // boş form
                                setDialogVisible(true);
                            }}
                        />
                    </div>

                    <FullServiceKmTable fullServiceId={id} onEditClick={handleEditKm} isCanUseFullService={isCanUseFullService} invoiceDetail={invoiceDetail?.data}/>

                    <FullServiceKmFormDialog
                        isCanUseFullService={isCanUseFullService}
                        isWarranty={isWarranty}
                        invoiceDetail={invoiceDetail?.data}
                        visible={dialogVisible}
                        onHide={() => setDialogVisible(false)}
                        initialData={editingRow}
                        invoiceId={id}
                        onSubmit={handleSaveKmRow}
                        onUpsertSilent={upsertSilent}
                        onChangeStatus={handleChangeStatus}
                        loading={dialogBusy}
                    />
                </div>
            )}
        </div>
    );
};

export default FullServiceCreateEditPage;
